/* eslint-disable no-restricted-syntax */
import config from 'js/app/config';

import { BADGE_NAMES } from 'bundles/product-card/utils/getProductCardStatusTags';
import { SEARCH_FACETS_FOR_URL_SYNC } from 'bundles/search-common/constants/filter';
import type { SearchProductType } from 'bundles/search-common/providers/searchTypes';
import type { SearchSortType } from 'bundles/search-common/types';

import _t from 'i18n!nls/search-common';

export const RECENT_SEARCHES_LOCAL_STORAGE_NAME = 'recent-searches';
export const RECENTLY_VIEWED_LOCAL_STORAGE_NAME = 'recently-viewed';
export const RECENT_ITEM_NUMBER_OF_DAYS_TO_LAST = 7;

export const SEARCH_RESULTS_HEADER_CLASS_NAME = 'rc-SearchResultsHeader';

export const TABLET_SCREEN_WIDTH = 992;
// unfortunately bound to a width defined in a styl file in another team's bundle.
// see static/bundles/page-header/components/__styles__/PageHeader.styl
export const MAX_SCREEN_WIDTH_FOR_MOBILE_SEARCH_BAR = 1023;
export const NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE = 12;
export const NUM_NO_RESULTS_TO_SHOW = 24;
export const NO_RESULTS_RECOMMENDATION_COLLECTION_ID = 'trending-courses';

export const NUMBER_OF_RESULTS_PER_PAGE = 12;
export const NUMBER_OF_RESULTS_FOR_DOMAIN_MOBILE_SEARCH = 3;

export const NUMBER_OF_RESULTS_PER_COLLECTION = 3;

export const THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE = 12;

export const SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING = 6;

export const SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING = 9;

export const SEARCH_RESULTS_BEFORE_ONBOARDING_FOR_FOUR_OR_TWO_CARD_PER_ROW = 8;

export const SEARCH_RESULT_ROWS_BEFORE_INLINE_AI_OVERVIEW = 2;

export const SEARCH_RESULT_ROWS_BEFORE_INLINE_NL_SUGGESTIONS = 2;

export const SEARCH_RESULT_ROWS_BEFORE_INLINE_ONBOARDING = 4;

export const SEARCH_RESULTS_BEFORE_UPSELL_CARD_DESKTOP = 7;
export const SEARCH_RESULTS_BEFORE_UPSELL_CARD_MOBILE = 3;

export const PRODUCT_TYPES: Record<string, SearchProductType> = {
  Course: 'COURSE',
  GuidedProject: 'GUIDED_PROJECT',
  Specialization: 'SPECIALIZATION',
  Project: 'PROJECT',
  ProfessionalCertificate: 'PROFESSIONAL_CERTIFICATE',
  Degree: 'DEGREE',
  UniversityCertificate: 'UNIVERSITY_CERTIFICATE',
  Mastertrack: 'MASTERTRACK',
  PostgraduateDiploma: 'POSTGRADUATE_DIPLOMA',
  GraduateCertificate: 'GRADUATE_CERTIFICATE',
  Video: 'VIDEO',
  Lesson: 'LESSON',
  LearningPath: 'LEARNING_PATH',
} as const;

/**
 * @deprecated: use PRODUCT_TYPES instead
 */
export const LEARNING_PRODUCTS = {
  Course: 'COURSE',
  GuidedProject: 'GUIDED PROJECT',
  Specialization: 'SPECIALIZATION',
  Project: 'PROJECT',
  ProfessionalCertificate: 'PROFESSIONAL CERTIFICATE',
  Degree: 'DEGREE',
  UniversityCertificate: 'UNIVERSITY CERTIFICATE',
  Mastertrack: 'MASTERTRACK',
  PostgraduateDiploma: 'POSTGRADUATE DIPLOMA',
  GraduateCertificate: 'GRADUATE CERTIFICATE',
  Video: 'VIDEO',
  Lesson: 'LESSON',
  LearningPath: 'LEARNING PATH',
  LearningPlan: 'LEARNING PLAN',
} as const;

export const LEARNING_PRODUCTS_ENUM_MAP: Record<string, (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS]> = {
  COURSE: 'COURSE',
  GUIDED_PROJECT: 'GUIDED PROJECT',
  SPECIALIZATION: 'SPECIALIZATION',
  PROJECT: 'PROJECT',
  PROFESSIONAL_CERTIFICATE: 'PROFESSIONAL CERTIFICATE',
  DEGREE: 'DEGREE',
  UNIVERSITY_CERTIFICATE: 'UNIVERSITY CERTIFICATE',
  MASTERTRACK: 'MASTERTRACK',
  POSTGRADUATE_DIPLOMA: 'POSTGRADUATE DIPLOMA',
  GRADUATE_CERTIFICATE: 'GRADUATE CERTIFICATE',
  VIDEO: 'VIDEO',
  LESSON: 'LESSON',
  LEARNING_PATH: 'LEARNING PATH',
} as const;

// AI Summary product card image dimensions
export const AI_SUMMARY_COMPACT_IMAGE_HEIGHT = 72;

/** Figma width of the AI Overview's suggested card on desktop and tablet. */
export const AI_OVERVIEW_CARD_WIDTH = '510px';
/** Suggested-card caps at the two intermediate design breakpoints. */
export const AI_OVERVIEW_CARD_WIDTH_NARROW = '494px';
export const AI_OVERVIEW_CARD_WIDTH_COMPACT = '408px';
/** The AI Overview stacks its summary, card and prompts below this width so the summary isn't squeezed. */
export const AI_OVERVIEW_STACKED_QUERY = '(max-width: 799.95px)';
export const AI_OVERVIEW_COMPACT_QUERY = '(max-width: 899.95px)';
export const AI_OVERVIEW_NARROW_QUERY = '(max-width: 1023.95px)';
export const AI_SUMMARY_MINI_IMAGE_SIZE = 64;
// Approximate rendered card width at 2x DPR for compact card image fetching
export const AI_SUMMARY_COMPACT_IMAGE_FETCH_WIDTH = 388;

export const getTranslatedProductName = (productName?: string) => {
  switch (productName) {
    case 'Course':
      return _t('Course');
    case 'Guided Project':
      return _t('Guided Project');
    case 'Project':
      return _t('Project');
    case 'Specialization':
      return _t('Specialization');
    case 'Professional Certificate':
      return _t('Professional Certificate');
    case 'Mastertrack':
      return _t('Mastertrack');
    case 'MasterTrack':
      return _t('MasterTrack');
    case 'Degree':
      return _t('Degree');
    case 'Post Graduate':
      return _t('Post Graduate');
    case 'Postgraduate Diploma':
      return _t('Postgraduate Diploma');
    case 'Graduate Certificate':
      return _t('Graduate Certificate');
    case 'University Certificate':
      return _t('University Certificate');
    case 'Video':
      return _t('Video');
    case 'Lesson':
      return _t('Lesson');
    case 'Learning Plan':
      return _t('Learning Plan');
    case 'Learning Path':
      return _t('Learning Path');
    default:
      return productName;
  }
};

export const NUMBER_OF_FILTER_ITEMS_TO_SHOW = 4;

export const PRODUCT_DIFFICULTY_LEVELS = {
  Beginner: 'Beginner',
  Intermediate: 'Intermediate',
  Advanced: 'Advanced',
  Mixed: 'Mixed', // Mixed represents a learning product that covers multiple difficulty levels.
} as const;
export const PRODUCT_DIFFICULTY_LEVELS_ENUM_MAP = Object.keys(PRODUCT_DIFFICULTY_LEVELS);
export const getTranslatedDifficulty = (difficulty: string): string => {
  switch (difficulty) {
    case 'Beginner':
    case 'BEGINNER':
      return _t('Beginner');
    case 'Intermediate':
    case 'INTERMEDIATE':
      return _t('Intermediate');
    case 'Advanced':
    case 'ADVANCED':
      return _t('Advanced');
    case 'Mixed':
    case 'MIXED':
      return _t('Mixed');
    default:
      return '';
  }
};

export const PRODUCT_DURATIONS = [
  'Under 60 Minutes', // UNDER_SIXTY_MINUTES
  'Less Than 2 Hours', // LESS_THAN_TWO_HOURS
  '1 - 3 Months', // ONE_TO_THREE_MONTHS
  '1 - 4 Weeks', // ONE_TO_FOUR_WEEKS
  '3 - 6 Months', // THREE_TO_SIX_MONTHS
  '6 - 12 Months', // SIX_TO_TWELVE_MONTHS
  '1 - 4 Years', // ONE_TO_FOUR_YEARS
];
export const PRODUCT_DURATIONS_STRING: Record<string, string> = {
  UNDER_SIXTY_MINUTES: 'Under 60 Minutes',
  LESS_THAN_TWO_HOURS: 'Less Than 2 Hours',
  ONE_TO_THREE_MONTHS: '1 - 3 Months',
  ONE_TO_FOUR_WEEKS: '1 - 4 Weeks',
  THREE_TO_SIX_MONTHS: '3 - 6 Months',
  SIX_TO_TWELVE_MONTHS: '6 - 12 Months',
  ONE_TO_FOUR_YEARS: '1 - 4 Years',
};
export const getTranslatedDurations = (productDuration: string) => {
  switch (productDuration) {
    case 'Under 60 Minutes':
      return _t('Under 60 Minutes');
    case 'Less Than 2 Hours':
      return _t('Less Than 2 Hours');
    case '1 - 3 Months':
      return _t('1 - 3 Months');
    case '1 - 4 Weeks':
      return _t('1 - 4 Weeks');
    case '3 - 6 Months':
      return _t('3 - 6 Months');
    case '6 - 12 Months':
      return _t('6 - 12 Months');
    case '1 - 4 Years':
      return _t('1 - 4 Years');
    default:
      return undefined;
  }
};

export const SEARCH_BAR_LOCATION = {
  SearchPage: 'SearchPage',
  SEOEntityPage: 'SEOEntityPage',
  FrontPage: 'FrontPage',
  FrontPageBanner: 'FrontPageBanner',
} as const;
export type SearchBarLocation = (typeof SEARCH_BAR_LOCATION)[keyof typeof SEARCH_BAR_LOCATION];

export const CONSUMER_SEARCH_ROUTE = 'search';
export const EQP_ROUTE = 'courses';

export const ON_SITE_SEARCH_PATH = `/${CONSUMER_SEARCH_ROUTE}`;

export const SEO_ENTITY_SEARCH_PATH = `/${EQP_ROUTE}`;

// Experiment route used for searh V2 in development.
export const DEV_ON_SITE_SEARCH_PATH = '/search-v2';

// On /courses, /search, /logged-in-home, /, and /programs/{programName} sync URL with search state.
export const ROUTES_TO_SYNC_SEARCH_STATE =
  /^(\/courses\/?|\/search\/?|\/search-v2\/?|\/logged-in-home\/?|\/?|\/programs\/.+)/;

export const SEARCH_STATE_URL_PARAMS = [...SEARCH_FACETS_FOR_URL_SYNC, 'query', 'page', 'sortBy'];

export const SEARCH_MAIN_INDEX_ID = 'search-results-all';
export const SEARCH_AUTOCOMPLETE_MAIN_INDEX_ID = 'autocomplete-results-all';
export const SEARCH_BROWSE_VALUE_PROP_INDEX_ID = 'search-results-browse-value-prop';
export const SEARCH_ENTERPRISE_AUTOCOMPLETE_CLIPS_INDEX_ID = 'search-results-clips';
export const SEARCH_ENTERPRISE_AUTOCOMPLETE_COURSES_S12N_INDEX_ID = 'search-results-courses-s12n';

export const BROWSE_IN_SEARCH_MOST_POPULAR_INDEX_ID = 'browse-in-search-most-popular';
export const BROWSE_IN_SEARCH_BEGINNER_INDEX_ID = 'browse-in-search-beginner';
export const BROWSE_IN_SEARCH_PARTNERS_INDEX_ID = 'browse-in-search-partners';
export const SEARCH_SUGGESTIONS_INDEX_ID = 'suggestions';

export const DEFAULT_SORTED_VALUE: SearchSortType = 'BEST_MATCH';
export const NEW_SORTED_VALUE: SearchSortType = 'NEW';
export const DEFAULT_PAGE_NUMBER = 1;
export const SORT_BY_FILTER = 'sortBy';

export const VALID_SORTED_VALUES = [DEFAULT_SORTED_VALUE, NEW_SORTED_VALUE];

export const ENTERPRISE_COLLECTION_ENTITIES = [
  'Short Form Content Video',
  'Courses',
  'Specializations',
  'Projects',
  'Guided Projects',
  'Professional Certificates',
];

export const SEARCHABLE_FILTER_GROUP_KEYS = ['partners', 'skills'];

export const COURSERA_PLUS_POPULAR_DOMAINS_MAPPING = [
  { id: 'business', name: 'Business' },
  { id: 'computer-science', name: 'Computer Science' },
  { id: 'data-science', name: 'Data Science' },
  { id: 'life-sciences', name: 'Health' },
  { id: 'information-technology', name: 'Information Technology' },
  { id: 'arts-and-humanities', name: 'Arts and Humanities' },
];

export const SHORT_FORM_CONTENT_ENTITY_TYPES = ['VIDEO', 'LESSON'];

// TODO(htran) remove after UCI APM Contentful migration is done in GNG-1259
export const UCI_APM_OBJECT_ID = 's12n~kLWnFWsyEeeVdhKUpvOPZg';
export const UCI_APM_IMAGE_URL = `${config.url.resource_assets}search/assets/uci_apm.png`;

export { DEFAULT_SEARCH_FACETS } from 'bundles/search-common/constants/filter';

export const OLD_TO_NEW_PARAM_MAP: Record<string, string> = {
  entityTypeDescription: 'productTypeDescription',
  productDurationEnum: 'productDuration',
};

export const BROWSE_IN_SEARCH_MOST_POPULAR_FACETFILTER_VALUES = [
  ['productTypeDescription:Specializations', 'productTypeDescription:Professional Certificates'],
];

export const BROWSE_IN_SEARCH_BEGINNER_FACETFILTER_VALUES = [['productDifficultyLevel:Beginner']];

export const MAX_NUMBER_OF_SPOTLIGHT_COLLECTION_PRODUCTS = 2;

export const SEARCH_STORE_OVERFLOW = 'search-overflow-hits';

export const SEARCH_UPSELL_TYPENAME = 'Search_UpsellHit';

export const REQUEST_ORIGIN_PAGE_TYPE = {
  SERP: 'SERP',
  EQP: 'EQP',
  SUGGESTIONS: 'SUGGESTIONS',
} as const;
export type RequestOriginPageType = (typeof REQUEST_ORIGIN_PAGE_TYPE)[keyof typeof REQUEST_ORIGIN_PAGE_TYPE];

export const REQUEST_ORIGIN_SEGMENT_TYPE = {
  CONSUMER: 'CONSUMER',
  ENTERPRISE: 'ENTERPRISE',
} as const;
export type RequestOriginSegmentType = (typeof REQUEST_ORIGIN_SEGMENT_TYPE)[keyof typeof REQUEST_ORIGIN_SEGMENT_TYPE];

export const AI_SUMMARY_CHIP_BUTTON_HEIGHT = 'var(--cds-spacing-400)';
// Default AI Overview shows the title plus about five body lines before "Show more".
export const AI_SUMMARY_COLLAPSED_HEIGHT = '160px';
// Two-panel AI Overview keeps the body preview to about two lines; with the
// lead-in title rendered above the clamp, that yields about three lines total.
export const AI_SUMMARY_TWO_PANEL_COLLAPSED_HEIGHT = 'var(--cds-spacing-600)';
export const AI_SUMMARY_TWO_PANEL_COLLAPSED_HEIGHT_PX = 48;
export const AI_SUMMARY_LINK_POSITION_PADDING = 20;
export const AI_SUMMARY_MINI_CARD_EXTRA_LINE_HEIGHT = 24;
// Product/design shorthand: the inline MINI AI Overview card is the "spearfishing" card.
export const AI_SUMMARY_SPEARFISHING_CARD_HEIGHT_ADJUSTMENT = -10;
export const AI_SUMMARY_MAX_HITS = 5;
export const AI_SUMMARY_MAX_SKILLS_PER_HIT = 3;
export const AI_SUMMARY_STREAMING_TIMEOUT_MS = 30_000;

export { SEARCH_AI_SUMMARY_SECTION, SEARCH_NL_SUGGESTIONS_SECTION } from 'bundles/search-common/constants/eventing';

export const ENTERPRISE_HIDDEN_BADGES = new Set([BADGE_NAMES.FREE]);
